// Areas that ship in later phases. The route exists so navigation, deep links and permissions can be
// exercised now; the page says plainly that nothing here is live ("Coming soon", with what the area
// will do and the closest live area). An area the role can't access renders permission-denied
// (direct links are not hidden by the sidebar filter).
import { useParams } from "@tanstack/react-router";
import { t } from "@ady/i18n";
import { StatePanel } from "@ady/ui";
import { TID } from "../testids";
import { DESIGN_SCREENS } from "../designs/screens";
import { AreaComingSoon } from "../shell/ComingSoon";
import { useShell } from "../shell/context";
import { can, itemForPath, type NavScope } from "../shell/nav";

export function PlaceholderPage({ scope }: { scope: NavScope }) {
  const { membership } = useShell();
  const params = useParams({ strict: false }) as { _splat?: string };
  const path = scope === "org" ? `settings/${params._splat ?? ""}` : (params._splat ?? "");
  const item = itemForPath(scope, path.replace(/\/$/, ""));
  if (!item) {
    return (
      <StatePanel
        kind="error"
        size="page"
        title={t("errors.notFound")}
        data-testid={TID.placeholderPage}
      />
    );
  }
  if (!can(membership, item.anyOf)) {
    return (
      <StatePanel
        kind="permission-denied"
        size="page"
        headingLevel={2}
        title={t("states.permissionDeniedTitle")}
        body={t("states.permissionDeniedBody", { who: t("states.permissionWhoOwner") })}
        data-testid={TID.placeholderPage}
      />
    );
  }
  // D-044: areas with a design-only screen show the layout instead of "Coming soon".
  const Design = DESIGN_SCREENS[item.id];
  if (Design) {
    return (
      <div
        className="flex w-full flex-col gap-4"
        data-testid={TID.placeholderPage}
        data-canonical={item.canonical}
        data-phase={item.phase}
        data-state="design-preview"
      >
        <Design item={item} />
      </div>
    );
  }
  return (
    <div
      className="flex w-full flex-col gap-4"
      data-testid={TID.placeholderPage}
      data-canonical={item.canonical}
      data-phase={item.phase}
      data-state="coming-soon"
    >
      <AreaComingSoon itemId={item.id} />
    </div>
  );
}
